import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ImagePlus } from "lucide-react";

import { brand } from "../theme/theme";

/** Hidden file input; resets after each pick so the same file can be chosen again. */
export function MediaInput({ inputRef, accept, multiple, onFiles }) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      multiple={multiple}
      hidden
      onChange={(event) => {
        onFiles(event.target.files);
        event.target.value = "";
      }}
    />
  );
}

MediaInput.propTypes = {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  onFiles: PropTypes.func.isRequired,
};

/** Dashed pink drop zone of the platform: drag files or pick them from the computer. */
export function MediaDropZone({
  onFiles,
  title,
  chip = null,
  accept = "image/*",
  multiple = false,
  isUploading = false,
  Icon = ImagePlus,
  minHeight = 132,
}) {
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsOver(false);
        onFiles(event.dataTransfer.files);
      }}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        minHeight,
        px: 2,
        py: minHeight < 132 ? 1.5 : 2.5,
        borderRadius: "12px",
        border: `1.5px dashed ${isOver ? brand.pink : brand.mediaBorder}`,
        background: isOver
          ? brand.soft
          : brand.mediaSurface,
        color: "#6B7280",
        textAlign: "center",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      {isUploading ? (
        <CircularProgress size={22} sx={{ color: brand.pink }} />
      ) : (
        <>
          <Icon size={18} color="#9CA3AF" />
          <Typography sx={{ fontSize: "0.9375rem", color: "#4B5563" }}>{title}</Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
            Arrastra aquí o{" "}
            <Box
              component="button"
              type="button"
              onClick={() => inputRef.current?.click()}
              sx={{
                border: "none",
                background: "none",
                p: 0,
                font: "inherit",
                color: "#374151",
                textDecoration: "underline",
                cursor: "pointer",
                "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2, borderRadius: "4px" },
              }}
            >
              busca en tu equipo
            </Box>
          </Typography>
        </>
      )}
      {chip && (
        <Box
          sx={{
            position: "absolute",
            left: 10,
            bottom: 10,
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            backgroundColor: "#fff",
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: "#374151",
            display: { xs: "none", sm: "block" },
          }}
        >
          {chip}
        </Box>
      )}
      <MediaInput inputRef={inputRef} accept={accept} multiple={multiple} onFiles={onFiles} />
    </Box>
  );
}

MediaDropZone.propTypes = {
  onFiles: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  chip: PropTypes.node,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  isUploading: PropTypes.bool,
  Icon: PropTypes.elementType,
  minHeight: PropTypes.number,
};

export default MediaDropZone;
